import React, { FC, ReactNode, useEffect, useRef } from 'react';

import classNames from 'classnames';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';

import { Icon } from '@shared/ui';

import cls from './CustomModal.module.css';
import { Loader } from './ui/Loader/Loader';

export interface ModalSubComponent {
    (props: { children: ReactNode; classes?: string }): JSX.Element;
}

export interface ModalBodyComponent {
    (props: { children: ReactNode; classes?: string; width?: string }): JSX.Element;
}

type ModalRootDataAttributes = {
    [key in `data-${string}`]?: string | number | boolean | null | undefined;
};

interface ModalComponent
    extends FC<
        {
            isOpen: boolean;
            onHideModal: () => void;
            children: ReactNode;
            zIndex?: number | null;
            classes?: string;
            needClose?: boolean;
            contentClasses?: string;
            closeButtonClass?: string;
            isLoading?: boolean;
            loader?: React.ReactNode;
            onAfterClose?: () => void;
        } & ModalRootDataAttributes
    > {
    Header: ModalSubComponent;
    Body: ModalBodyComponent;
    Footer: ModalSubComponent;
}

const Header = ({ children, classes }: { children: React.ReactNode; classes?: string }) => (
    <div className={classNames(cls.customModalHeader, classes)}>{children}</div>
);

const Body = ({
    width = '100%',
    children,
    classes,
}: {
    width?: string;
    children: React.ReactNode;
    classes?: string;
}) => (
    <div style={{ width }} className={classNames(cls.customModalBody, classes)}>
        {children}
    </div>
);

const Footer = ({ children, classes }: { children: React.ReactNode; classes?: string }) => (
    <div className={classNames(cls.customModalFooter, classes)}>{children}</div>
);

export const CustomModal: ModalComponent = ({
    isOpen = false,
    onHideModal,
    children,
    zIndex = null,
    classes,
    needClose = true,
    contentClasses,
    closeButtonClass,
    isLoading = false,
    loader,
    onAfterClose,
    ...rootDataAttributes
}) => {
    const nodeRef = useRef(null);
    const onHideRef = useRef(onHideModal);

    const contentRef = useRef<HTMLDivElement>(null);
    const mouseDownInside = useRef(false);

    useEffect(() => {
        onHideRef.current = onHideModal;
    }, [onHideModal]);

    useEffect(() => {
        if (!isOpen) return;

        const html = document.documentElement;
        const hasVerticalScrollbar =
            document.documentElement.scrollHeight > document.documentElement.clientHeight;

        if (isOpen) {
            const originalBodyOverflow = document.body.style.overflow;
            const originalHtmlOverflow = html.style.overflow;
            const originalScrollBarGutter = html.style.scrollbarGutter;
            const originalBackground = html.style.background;

            if (hasVerticalScrollbar) {
                html.style.scrollbarGutter = 'stable';
            }

            document.body.style.overflow = 'hidden';
            html.style.overflow = 'hidden';
            html.style.background = 'rgb(0 0 0 / 40%)';

            return () => {
                document.body.style.overflow = originalBodyOverflow;
                html.style.overflow = originalHtmlOverflow;
                html.style.scrollbarGutter = originalScrollBarGutter;
                html.style.background = originalBackground;
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleMouseDown = (e: MouseEvent) => {
            mouseDownInside.current =
                !!contentRef?.current && contentRef?.current?.contains(e.target as Node);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (e.defaultPrevented) return;

            e.preventDefault();
            onHideRef.current?.();
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', onKeyDown, { capture: true });

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', onKeyDown, { capture: true } as any);
        };
    }, [isOpen, onHideModal]);

    const handleOverlayClick = () => {
        if (mouseDownInside.current) {
            mouseDownInside.current = false;
            return;
        }
        onHideModal();
    };

    return ReactDOM.createPortal(
        <CSSTransition
            in={isOpen}
            timeout={300}
            classNames={{
                enter: cls.modalEnter,
                enterActive: cls.modalEnterActive,
                exit: cls.modalExit,
                exitActive: cls.modalExitActive,
            }}
            unmountOnExit
            nodeRef={nodeRef}
            onExited={onAfterClose}
        >
            <div
                className={classNames(cls.customModal, { [cls.zIndex]: zIndex })}
                style={zIndex ? { zIndex } : undefined}
                ref={nodeRef}
                data-ui-modal="true"
                {...rootDataAttributes}
            >
                <div
                    className={classNames(cls.customModalOverlay, classes)}
                    onClick={handleOverlayClick}
                >
                    <div
                        className={classNames(cls.customModalContent, contentClasses)}
                        onClick={(e) => e.stopPropagation()}
                        ref={contentRef}
                    >
                        {isLoading && <>{loader || <Loader variant="light" />}</>}
                        {needClose && (
                            <button
                                className={classNames(cls.customModalClose)}
                                onClick={onHideModal}
                            >
                                <Icon iconName="closeModal" />
                            </button>
                        )}
                        {children}
                    </div>
                </div>
            </div>
        </CSSTransition>,
        document.body
    );
};

CustomModal.Header = Header;
CustomModal.Body = Body;
CustomModal.Footer = Footer;
